/**
 * @typedef {import('BMapsModel').Compound} Compound
 * @typedef {import('BMapsModel').MapCase} MapCase
 * @typedef {import('BMapsModel').ParentCompound} ParentCompound
 * @typedef {import('@mui/x-data-grid').GridColumnVisibilityModel} GridColumnVisibilityModel
 * @typedef {import('@mui/x-data-grid').GridRowsProp} GridRowsProp
 * @typedef {import('@mui/x-data-grid').GridColDef} GridColDef
 * @typedef {import('@mui/x-data-grid').GridColumnGroupingModel} GridColumnGroupingModel
 * @typedef {import('@mui/x-data-grid').GridToolbarProps} GridToolbarProps
 * @typedef {import('@mui/x-data-grid').ToolbarPropsOverrides} ToolbarPropsOverrides
 * @typedef {any} item
 * @typedef {import('@mui/x-data-grid').GridSlotProps['filterPanel']} FilterPanelProps
 */
import Box from '@mui/material/Box';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    DataGrid, GridColumnHeaderSortIcon, GridColumnsPanel,
    GridFilterPanel, useGridApiRef,
} from '@mui/x-data-grid';
import { GridToolBar } from './CustomToolbar';
import BMapsTooltip from '../BMapsTooltip';

/**
 * @param {{
 *     rows: GridRowsProp,
 *     columns: GridColDef[],
 *     columnGroupingModel?: GridColumnGroupingModel,
 *     setColumnVisibilityModel?: (columnVisibilityModel: GridColumnVisibilityModel) => any,
 *     columnVisibilityModel?: GridColumnVisibilityModel,
 *     setCheckedRowItems?: (item: item) => any,
 *     rowItems?: item[],
 *     disableTable?: boolean
 *     exportFileName?: string
 *     checkboxSelection?: boolean
 *     toolBarId? string,
 *     customPanel?: (props: GridColumnsPanelProps) => any
 *     visibilityDefault?: boolean
 *     duplicateColumnKeys?: string[]
 *}} props
 * @returns
 */
export function BMapsTable({
    rows, columns, columnGroupingModel,
    setColumnVisibilityModel, columnVisibilityModel,
    setCheckedRowItems, rowItems, disableTable, exportFileName,
    checkboxSelection, toolBarId, customPanel = null, visibilityDefault = false,
    duplicateColumnKeys = [], availableCharts, setChart,
}) {
    const apiRef = useGridApiRef();

    const managedVisibilityModel = manageColumnVisibility(
        columnVisibilityModel, columns, duplicateColumnKeys, visibilityDefault
    );
    return (
        <DataGrid
            rows={rows}
            columns={columns}
            columnGroupingModel={columnGroupingModel}
            checkboxSelection={checkboxSelection}
            disableRowSelectionOnClick
            slots={{
                toolbar: (toolBarProps) => (
                    <GridToolBar
                        {...toolBarProps}
                        exportFileName={exportFileName}
                        toolBarId={toolBarId}
                        charts={availableCharts}
                        setChart={setChart}
                    />
                ),
                filterPanel: () => (
                    <CustomFilterPanel />
                ),
                filterPanelDeleteIcon: () => (
                    <DeleteIcon />
                ),
                columnsPanel: customPanel ? (props) => customPanel(props)
                    : (props) => <GridColumnsPanel {...props} />,
                columnHeaderSortIcon: (props) => {
                    const found = columns.find((c) => c.field === props.field);
                    const title = found ? `Sort by ${found.headerName}` : 'Sort';
                    return (
                        <BMapsTooltip title={title} noMaxWidth>
                            <GridColumnHeaderSortIcon {...props} />
                        </BMapsTooltip>
                    );
                },
            }}
            slotProps={{
                columnsManagement: {
                    getTogglableColumns: (cols) => cols.filter(({ field }) => {
                        // '__check__' is the default name for the checkboxes given by
                        // mui data grid. This will prevent users from removing the
                        // checkbox column
                        if (field === '__check__') return false;
                        return true;
                    }).map((col) => col.field),
                },
            }}
            hideFooter
            apiRef={apiRef}
            loading={disableTable}
            onRowSelectionModelChange={(gridRows) => {
                const newCheckedItems = [];
                gridRows.forEach((index) => {
                    if (rowItems[index]) {
                        newCheckedItems.push(rowItems[index]);
                    }
                });
                setCheckedRowItems(newCheckedItems);
            }}
            onColumnVisibilityModelChange={(event) => {
                setColumnVisibilityModel(event);
            }}
            columnVisibilityModel={managedVisibilityModel}
            sx={{
                '& .protein-header': {
                    backgroundColor: 'lightGray',
                    borderRight: '1px solid white',
                    borderLeft: '1px solid white',
                },
                '& .MuiDataGrid-columnHeader--emptyGroup': {
                    borderBottom: '1px solid lightgray',
                    '& .MuiDataGrid-columnSeparator': {
                        display: 'none',
                    },
                },
                '& .none-header': {
                    padding: 0,
                },
                // on some resolutions this separator causes the top check box to become
                // miss-aligned.
                '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnSeparator': {
                    display: 'none',
                },
                // Changes for custom header menu button
                // 1. Hide the default menu icon button
                //    Hidden instead of disabled because the disabled flag
                //    is consulted when displaying the "hide column" menu option.
                '& .MuiDataGrid-menuIconButton': { display: 'none' },
                // 2. Display the custom menu button
                //    Only appears on hover of column header OR when the menu is expanded.
                //    Hidden with width+visibility, instead of display, to avoid jumping tooltip.
                '& .compoundTable-customMenuButton': {
                    width: 0,
                    visibility: 'hidden',
                },
                '& .MuiDataGrid-columnHeader:hover': {
                    '.compoundTable-customMenuButton': {
                        width: 'unset',
                        visibility: 'visible',
                    },
                },
                '& .MuiDataGrid-columnHeader .compoundTable-customMenuButton[aria-expanded="true"]': {
                    width: 'unset',
                    visibility: 'visible',
                },
                // 3. Make sure sort icon is visible when menu is open, not just on hover.
                //    This is the default behavior when using the default column menu.
                //    The columnHeader needs to adjust visibility and width,
                //    and the button itself need to adjust opacity.
                '& .MuiDataGrid-columnHeader:has(*[aria-expanded="true"]) .MuiDataGrid-iconButtonContainer:has(.MuiDataGrid-sortButton)': {
                    visibility: 'visible',
                    width: 'unset',
                },
                // Opacity depends on whether the column has an active sort (100%) or not (50%)
                '& .MuiDataGrid-columnHeader:has(*[aria-expanded="true"]) .MuiDataGrid-sortButton': {
                    opacity: 1,
                },
                '& .MuiDataGrid-columnHeader:has(*[aria-expanded="true"]):not(.MuiDataGrid-columnHeader--sorted) .MuiDataGrid-sortButton': {
                    opacity: 0.5,
                },
            }}
        />
    );
}
function CustomFilterPanel() {
    const filterPanelProps = {
        // Display columns by ascending alphabetical order
        columnsSort: 'asc',
        filterFormProps: {
            // Customize inputs by passing props
            logicOperatorInputProps: {
                variant: 'outlined',
            },
            columnInputProps: {
                variant: 'outlined',
                sx: { mt: 'auto' },
            },
            operatorInputProps: {
                variant: 'outlined',
                sx: { mt: 'auto' },
            },
            valueInputProps: {
                InputComponentProps: {
                    variant: 'outlined',
                    sx: { mt: 'auto' },
                },
            },
            deleteIconProps: {
                sx: {
                    margin: '.5rem',
                },
            },
        },
        sx: {
            // Customize inputs using css selectors
            '& .MuiDataGrid-filterForm': { p: 2 },
            '& .MuiDataGrid-filterForm:nth-child(even)': {
                backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#444' : '#f5f5f5'),
            },
            '& .MuiDataGrid-filterFormLogicOperatorInput': { mr: 2 },
            '& .MuiDataGrid-filterFormColumnInput': { mr: 2, width: 150 },
            '& .MuiDataGrid-filterFormOperatorInput': { mr: 2 },
            '& .MuiDataGrid-filterFormValueInput': { width: 200 },
            '& .MuiIconButton-root': {
                margin: 'auto',
            },
        },
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <GridFilterPanel {...filterPanelProps} />
        </Box>
    );
}

// Provides additional functionality to column visibility
// columns can be hidden by default if visibilityDefault is false
// column visibility can be linked to other columns by strings that are common in multiple columns
function manageColumnVisibility(
    compoundVisibilityModel, allColumns, duplicateColumnKeys, visibilityDefault
) {
    const managedVisibilityModel = { ...compoundVisibilityModel };
    const refrenceVisibility = {};

    Object.keys(managedVisibilityModel).forEach((key) => {
        const refrenceTitle = duplicateColumnKeys.find((title) => key.includes(title));
        if (refrenceTitle) {
            refrenceVisibility[refrenceTitle] = managedVisibilityModel[key];
        }
    });

    allColumns.forEach((column) => {
        Object.keys(refrenceVisibility).forEach((key) => {
            if (column.field.includes(key)) {
                managedVisibilityModel[column.field] = refrenceVisibility[key];
            }
        });
    });

    if (!visibilityDefault) {
        allColumns.forEach((column) => {
            if (!managedVisibilityModel[column.field]) {
                managedVisibilityModel[column.field] = false;
            }
        });
    }

    return managedVisibilityModel;
}
